<template>
  <el-dialog v-model="isVisible"
  :title="getI18n(data.title)"
  :width="data.dialogWidth"
  :before-close="handleClose"
  :draggable="true"
  :close-on-click-modal="false"
  >
    <el-scrollbar max-height="100vh" style="padding-right: 15px;">
      <el-form>
        <template v-for="(item) in refItemList" :key="item.key">
          <el-form-item :label="getI18n(item.label)" :label-width="data.formLabelWidth+'px'" :class="{ 'required-field': item.isRequired }">
            <component :is="item.tag" v-bind="item" v-model="modelValues[item.key]"></component>
          </el-form-item>
        </template>
      </el-form>
      <div class="button-container">
        <el-button @click="onCancel()">{{getI18n('取消')}}</el-button>
        <el-button type="primary" @click="onOk()">{{ data.editOkBtnName ? getI18n(data.editOkBtnName) : getI18n('确定') }}</el-button>
      </div>
    </el-scrollbar>
  </el-dialog>
</template>

<script lang="ts">
import { ref, defineComponent, defineProps, onMounted, defineEmits } from 'vue'
import { apiPostData, getI18n, isEmptyString, deepCopy, isEmptyArray, showRequestResult, combinateUrlQueryStr } from '../utils'
import { qrcodeUrl } from '../config'
import store from '../store'

import DropDownSelector from './DropDownSelector.vue'
import CascaderSelector from './CascaderSelector.vue'
import DatePicker from './DatePicker.vue'
import ImageItem from './ImageItem.vue'
import SwitchItem from './SwitchItem.vue'
import UploadSingleFileKit from './UploadSingleFileKit.vue'

import { ElMessage } from 'element-plus'

export default defineComponent({
  name: 'TableDataDlg',
  components: {
    DropDownSelector,
    CascaderSelector,
    ImageItem,
    DatePicker,
    UploadSingleFileKit,
    SwitchItem
  }
})
</script>

<script lang="ts" setup>
const props = defineProps({
  // dialog 的数据
  data: {
    type: Object,
    require: true
  },
  // 这变量单独剥离出来，方便控制窗口的显示
  dialogIndex: {
    type: Number,
    required: true
  }
})

// eslint-disable-next-line
const modelValues = ref({} as {[key: string]: any})
const refItemList = ref([])
const isVisible = ref(true)

onMounted(async () => {
  refItemList.value = deepCopy(props.data.itemList)
  if (isEmptyArray(refItemList.value)) {
    return
  }
  modelValues.value = {}

  // 构建每行的查询参数
  const queryStringArr: string[] = []
  for (let i = 0; i < refItemList.value.length; i++) {
    const key = refItemList.value[i].key
    const dataValue = refItemList.value[i].dataValue
    if (dataValue === undefined) {
      continue
    }
    refItemList.value[i].dataValue = String(refItemList.value[i].dataValue)
    modelValues.value[key] = String(dataValue)
    if (!isEmptyString(dataValue) && typeof dataValue === 'string' && !dataValue.startsWith('http://') && !dataValue.startsWith('https://')) {
      queryStringArr.push(`${key}=${dataValue}`)
    }
  }
  const queryString = queryStringArr.join('&')

  console.log('TableDataDlg onMounted')
  // 传个每个元素的值
  for (let i = 0; i < refItemList.value.length; i++) {
    if (!isEmptyString(refItemList.value[i].srcDataUrl)) {
      const url = combinateUrlQueryStr(refItemList.value[i].srcDataUrl, queryString)
      refItemList.value[i].srcDataUrl = url
    }
    if (refItemList.value[i].tagInEditDlg === 'ImageItem') {
      let url = ''
      if (refItemList.value[i].prop === 'qrcode__') {
        if (isEmptyString(refItemList.value[i].dataValue)) {
          url = combinateUrlQueryStr(qrcodeUrl, queryString)
        } else {
          url = combinateUrlQueryStr(refItemList.value[i].dataValue, queryString)
        }
      } else {
        url = combinateUrlQueryStr(refItemList.value[i].dataValue, queryString)
      }
      url = combinateUrlQueryStr(url, `authorizationToken=${store.state.authorizationToken}`)
      refItemList.value[i].dataValue = url
    }
  }
})
const emits = defineEmits<
{(e: 'onOk', props: object, data: object, response: object): void,
(e: 'onCancel', props: object): void
}>()

let isDealing = false
function setKeyValue (data:object, key:string, value:string) {
  if (value === undefined) {
    data[key] = value
  } else if (typeof value !== 'string') {
    data[key] = value
  } else {
    if (!value.startsWith('__uploade&&&')) {
      data[key] = value
    } else {
      // 1. 以 "__uploade&&&" 开头, 上传文件
      // 2. 按 "&&&" 分割字符串
      const parts = value.split('&&&')
      data[key] = parts[1]
      data[key + '__ori_filename'] = parts[2]
      data[key + '__file_ori_hash'] = parts[3]
    }
  }
}
function onOk () {
  if (isDealing) {
    return
  }
  isDealing = true
  // eslint-disable-next-line
  const data = {} as {[key: string]: any}
  // eslint-disable-next-line
  const itemList = props.data!.itemList
  for (let i = 0; i < itemList.length; i++) {
    const key = itemList[i].key
    if (itemList[i].filedType !== undefined) {
      if (itemList[i].filedType === 'number') {
        data[key] = Number(modelValues.value[key])
      } else {
        // data[key] = modelValues.value[key]
        setKeyValue(data, key, modelValues.value[key])
      }
    } else {
      // data[key] = modelValues.value[key]
      setKeyValue(data, key, modelValues.value[key])
    }
  }

  ElMessage({
    type: 'success',
    message: 'send request to server'
  })

  // eslint-disable-next-line
  apiPostData(props.data!.url, data)
    .then(response => {
      const prefixOk = data.editOkBtnName ? data.editOkBtnName : getI18n('确定')
      showRequestResult(response, prefixOk, prefixOk, getI18n('请求结果'), true, true, true, (result) => {
        isDealing = false
        if (result?.success) {
          emits('onOk', props, response, result)
        }
      })
    }).catch(error => {
      console.error('Error:', error)
      isDealing = false
    })
}
function onCancel () {
  emits('onCancel', props)
}
const handleClose = (done: () => void) => {
  emits('onCancel', props)
}
</script>

<style>
.el-dialog {
  display: flex !important;
  flex-direction: column !important;
  margin: 1px 0px 0px 0px !important;
  margin-top: 40px !important;
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
}
.el-dialog__header {
    padding: 10px 0px 0px 20px !important;
    padding-bottom: 1px !important;
    margin-right: 1px !important;
}

.el-dialog__body {
    padding: 15px !important;
}

.el-form-item {
    margin-bottom: 8px !important;
}

.required-field .el-form-item__label::before {
  content: "*";
  color: red;
  margin-right: 4px;
}
.button-container {
    display: flex;
    justify-content: flex-end;
    gap: 0px;
    margin-top: 0px;
}
</style>
